/* eslint-disable react/prop-types */
import {
    createContext,
    ReactChild,
    useContext,
    useEffect,
    useState
} from "react";

import { useConfig } from "@atlassiansox/bxp-content-framework";

import { getCookie } from "../../utils";

import { useMember } from "./use-member";

declare global {
    interface Window {
        OTWrapperGetIdentifier?: () => string;
    }
}

export type OnetrustContextType = {
    readonly hasOptedOut?: boolean;
    readonly privacyLink?: ReactChild;
};

export const OnetrustContext = createContext<OnetrustContextType>({
    hasOptedOut: true,
    privacyLink: <></>
});

export const useOnetrust = () => {
    const context = useContext(OnetrustContext);

    if (context === undefined) {
        throw new Error(
            "`useOnetrust` must be used within a `Onetrust.Provider`"
        );
    }
    return context;
};

export const OnetrustProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const { environment } = useConfig();
    const member = useMember();

    const [hasOptedOut, setHasOptedOut] = useState(true);

    const privacyLink = (
        <li className="atl-policy-link-container">
            <a href="http://" className="atl-policy-link atl-policy-link-text">
                Notice at Collection
            </a>
        </li>
    );

    useEffect(() => {
        // to be removed when the migration to cloud.session.token is complete
        if (member.status === "pending") return;

        if (member.status === "resolved") {
            window.OTWrapperGetIdentifier = () => {
                return member.value.aaId;
            };
        }

        const cookieName = "OptanonConsent";
        const optanonConsentCookieValue = getCookie(cookieName);

        setHasOptedOut(
            optanonConsentCookieValue
                ? optanonConsentCookieValue.includes(",2:0")
                : false
        );

        if (!document.head.querySelector("#Onetrust")) {
            const scriptId = "Onetrust";
            const script = document.createElement("script");
            script.async = true;
            script.type = "text/javascript";
            script.id = scriptId;
            if (environment === "production") {
                script.src =
                    "https://atl-onetrust-wrapper.atlassian.com/assets/atl-onetrust-wrapper.min.js";
                script.setAttribute(
                    "data-domain",
                    "dcb287ca-43c0-462c-a090-46b2debf69e2"
                );
                script.setAttribute("data-test", "false");
            } else {
                script.src =
                    "https://atl-onetrust-wrapper.stg-east.frontend.public.atl-paas.net/assets/atl-onetrust-wrapper.min.js";
                script.setAttribute(
                    "data-domain",
                    "dc680e5f-1f51-48c1-8bd3-d6db0aeece4a"
                );
                script.setAttribute("data-test", "true");
            }
            script.setAttribute("data-geofence", "false");
            script.setAttribute("data-sync", "true");

            document.head.appendChild(script);
        }
    }, [environment, member, hasOptedOut]);

    return (
        <OnetrustContext.Provider value={{ hasOptedOut, privacyLink }}>
            {children}
        </OnetrustContext.Provider>
    );
};
